"use client";

import { Logo } from "@/components/logo";
import { useRumPageView } from "@/components/rum-provider";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { signIn, signUp } from "@/lib/auth-client";
import { SIGNUP_DISABLED } from "@/lib/config";
import { isValidCallbackURL } from "@/lib/url";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const signupSchema = z.object({
	name: z.string().min(1, "Name is required"),
	email: z.string().min(1, "Email is required").email("Invalid email address"),
	password: z.string().min(8, "Password must be at least 8 characters"),
});

type SignupFormValues = z.infer<typeof signupSchema>;

function SignupContent() {
	const searchParams = useSearchParams();
	const rawCallback = searchParams.get("callbackURL") ?? "/dashboard";
	const callbackURL = isValidCallbackURL(rawCallback)
		? rawCallback
		: "/dashboard";
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [emailSent, setEmailSent] = useState(false);
	useRumPageView("signup_page_viewed");

	const form = useForm<SignupFormValues>({
		resolver: zodResolver(signupSchema),
		defaultValues: {
			name: "",
			email: "",
			password: "",
		},
	});

	const email = form.watch("email");

	async function onSubmit(values: SignupFormValues) {
		setError("");
		setLoading(true);

		const { error } = await signUp.email({
			name: values.name,
			email: values.email,
			password: values.password,
			callbackURL: `${window.location.origin}${callbackURL}`,
		});

		if (error) {
			setError(error.message ?? "Failed to create account");
			setLoading(false);
			return;
		}

		setEmailSent(true);
		setLoading(false);
	}

	async function handleGoogleSignIn() {
		setLoading(true);
		await signIn.social({
			provider: "google",
			callbackURL: `${window.location.origin}${callbackURL}`,
		});
	}

	// Invited teammates are exempt from the kill switch server-side, so the form
	// stays available when arriving from an invitation. The API is still the
	// authority: a crafted callbackURL without a real pending invitation is
	// rejected by databaseHooks.user.create.before.
	const invitedSignup = callbackURL.startsWith("/invite/");

	if (SIGNUP_DISABLED && !invitedSignup) {
		return (
			<div className="bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl rounded-3xl p-8 md:p-10 animate-fade-up">
				<div className="text-center">
					<div className="flex justify-center mb-6">
						<Logo size="lg" />
					</div>
					<h1 className="font-display font-bold text-2xl md:text-3xl text-gray-900 mb-3">
						Signups paused
					</h1>
					<p className="text-gray-600">
						We&apos;ve temporarily paused new signups. Please check back soon.
					</p>
				</div>
				<div className="mt-8 pt-6 border-t border-gray-100 text-center">
					<p className="text-sm text-gray-500">
						Already have an account?{" "}
						<Link
							href={
								callbackURL !== "/dashboard"
									? `/login?callbackURL=${encodeURIComponent(callbackURL)}`
									: "/login"
							}
							className="font-semibold text-brand-blue hover:text-indigo-600"
						>
							Sign in
						</Link>
					</p>
				</div>
			</div>
		);
	}

	if (emailSent) {
		return (
			<div className="bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl rounded-3xl p-8 md:p-10 animate-fade-up">
				<div className="text-center">
					<div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
						<Mail className="w-8 h-8 text-green-600" />
					</div>
					<h1 className="font-display font-bold text-2xl md:text-3xl text-gray-900 mb-3">
						Check your email
					</h1>
					<p className="text-gray-600 mb-2">We sent a verification link to</p>
					<p className="font-semibold text-gray-900 mb-6">{email}</p>
					<p className="text-sm text-gray-500">
						Click the link in your email to verify your account and sign in.
					</p>
				</div>
				<div className="mt-8 pt-6 border-t border-gray-100 text-center">
					<p className="text-sm text-gray-500">
						Didn&apos;t receive the email?{" "}
						<Link
							href={
								callbackURL !== "/dashboard"
									? `/login?callbackURL=${encodeURIComponent(callbackURL)}`
									: "/login"
							}
							className="font-semibold text-brand-blue hover:text-indigo-600"
						>
							Try signing in
						</Link>
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl rounded-3xl p-8 md:p-10 animate-fade-up">
			<div className="text-center mb-8">
				<div className="flex justify-center mb-6">
					<Logo size="lg" />
				</div>
				<h1 className="font-display font-bold text-2xl md:text-3xl text-gray-900 mb-2">
					Create account
				</h1>
				<p className="text-gray-600">Start sending emails in minutes</p>
			</div>

			{error && (
				<div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-sm text-red-600 flex items-center justify-center">
					{error}
				</div>
			)}

			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
					<FormField
						control={form.control}
						name="name"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="text-sm font-semibold text-gray-700 ml-1">
									Full name
								</FormLabel>
								<FormControl>
									<Input
										type="text"
										placeholder="John Doe"
										className="h-12 bg-gray-50 border-transparent focus:bg-white focus:border-brand-blue/30 focus:ring-4 focus:ring-brand-blue/10 rounded-xl transition-all"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="text-sm font-semibold text-gray-700 ml-1">
									Email
								</FormLabel>
								<FormControl>
									<Input
										type="email"
										placeholder="you@example.com"
										className="h-12 bg-gray-50 border-transparent focus:bg-white focus:border-brand-blue/30 focus:ring-4 focus:ring-brand-blue/10 rounded-xl transition-all"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="password"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="text-sm font-semibold text-gray-700 ml-1">
									Password
								</FormLabel>
								<FormControl>
									<Input
										type="password"
										placeholder="Min. 8 characters"
										className="h-12 bg-gray-50 border-transparent focus:bg-white focus:border-brand-blue/30 focus:ring-4 focus:ring-brand-blue/10 rounded-xl transition-all"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<Button
						type="submit"
						className="w-full h-12 bg-brand-blue hover:bg-indigo-600 text-white font-bold rounded-xl shadow-lg shadow-brand-blue/20 transition-all transform active:scale-95"
						disabled={loading}
					>
						{loading ? "Creating account..." : "Create account"}
					</Button>
				</form>
			</Form>

			<div className="relative my-8">
				<div className="absolute inset-0 flex items-center">
					<div className="w-full border-t border-gray-100" />
				</div>
				<div className="relative flex justify-center">
					<span className="bg-white/50 backdrop-blur px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
						Or continue with
					</span>
				</div>
			</div>

			<Button
				type="button"
				variant="outline"
				className="w-full h-12 bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-gray-700 font-semibold rounded-xl transition-all"
				onClick={handleGoogleSignIn}
				disabled={loading}
			>
				<svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" aria-label="Google">
					<title>Google</title>
					<path
						fill="#4285F4"
						d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
					/>
					<path
						fill="#34A853"
						d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
					/>
					<path
						fill="#FBBC05"
						d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
					/>
					<path
						fill="#EA4335"
						d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
					/>
				</svg>
				Continue with Google
			</Button>

			<p className="mt-8 text-center text-sm text-gray-500">
				Already have an account?{" "}
				<Link
					href={
						callbackURL !== "/dashboard"
							? `/login?callbackURL=${encodeURIComponent(callbackURL)}`
							: "/login"
					}
					className="font-bold text-brand-blue hover:text-indigo-600 transition-colors"
				>
					Sign in
				</Link>
			</p>

			<p className="mt-6 text-center text-xs text-gray-400">
				By signing up, you agree to our{" "}
				<Link href="/terms" className="hover:text-gray-600 transition-colors">
					Terms
				</Link>{" "}
				and{" "}
				<Link href="/privacy" className="hover:text-gray-600 transition-colors">
					Privacy Policy
				</Link>
			</p>
		</div>
	);
}

function SignupFallback() {
	return (
		<div className="bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl rounded-3xl p-8 md:p-10 animate-pulse">
			<div className="h-8 w-32 bg-gray-200 rounded mx-auto mb-6" />
			<div className="h-6 w-40 bg-gray-200 rounded mx-auto mb-2" />
			<div className="h-4 w-48 bg-gray-200 rounded mx-auto mb-8" />
			<div className="space-y-4">
				<div className="h-12 bg-gray-100 rounded-xl" />
				<div className="h-12 bg-gray-100 rounded-xl" />
				<div className="h-12 bg-gray-100 rounded-xl" />
				<div className="h-12 bg-gray-200 rounded-xl" />
			</div>
		</div>
	);
}

export default function SignupPage() {
	return (
		<Suspense fallback={<SignupFallback />}>
			<SignupContent />
		</Suspense>
	);
}
